import { Component, createRef } from "react"

/* Components */
import { DataLayout, Pagination, Icon, Avatar } from "../../components"

/* Widgets */
import { MasterModal } from "../../widgets/master"

/* Helpers */
import { withRouter } from "../../helpers"

/* Shared */
import { roleOf, rolesOf, ratingText } from "../../widgets/master/shared"
import { ago, initialsOf, avatarColorOf } from "../../widgets/user/shared"
import { plural } from "../../widgets/category/shared"
import { money } from "../../widgets/service/shared"

/* Constants */
import { env } from "../../constants"

/* REST API */
import { list } from "../../api/Master"


/* The orders of the list */
const SORTS = [
    { key: "new", text: "Новые" },
    { key: "month", text: "За 30 дней" },
    { key: "bookings", text: "Больше записей" },
    { key: "rating", text: "Рейтинг" },
    { key: "name", text: "А — Я" }
]

/* The states of the list */
const STATES = [
    { key: "all", text: "Все" },
    { key: "team", text: "В команде" },
    { key: "left", text: "Ушли" },
    { key: "none", text: "Без точки" }
]

/* The empty filters */
const INITIAL = { search: "", role: "", state: "all", activity: "", sort: "new" }


/* Page Masters — everyone with a Bookify Pro account across the points: where they work and in
   which role, how busy they are and how the clients rate them. The card of a master opens over the list */
class Masters extends Component {

    constructor() {
        super()

        this.state = {
            status: "loading",
            data: [],
            count: 0,
            limit: 30,
            page: 1,
            statistics: null,
            leaders: [],
            ...INITIAL
        }

        this._master = createRef()
        this._timer = null
    }

    componentDidMount = () => {

        window.scrollTo(0, 0)
        this.load(1, true)
    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
    }

    load = (page = 1, reload = false) => {

        /* Fields */
        const { search, role, state, activity, sort } = this.state

        if (reload) {
            this.setState({ status: "loading" })
        }

        this.setState({ page })

        list({ page, search, role, state, activity, sort }).then(response => {
            if (response && response.status === 200) {
                this.setState({
                    status: "ok",
                    data: response.data.list,
                    count: response.data.count,
                    limit: response.data.limit,
                    statistics: response.data.statistics,
                    leaders: response.data.leaders
                })
            }
            else {
                this.setState({ status: "error", data: [], count: 0 })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0 })
        })
    }

    /* Every filter starts from the first page */
    refilter = fields => {
        this.setState(fields, () => this.load(1, true))
    }

    /* The search waits for the hand to stop */
    searchText = text => {

        clearTimeout(this._timer)
        this.setState({ search: text })

        this._timer = setTimeout(() => this.load(1, true), text.length === 0 ? 0 : 450)
    }

    /* A filter pressed twice lets go */
    toggle = (field, value, empty) => {
        this.refilter({ [field]: this.state[field] === value ? empty : value })
    }

    _header = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="organization-header">

                <div className="page-header-text">Мастера</div>

                <div className="organization-header-tools">
                    <form onSubmit={event => { event.preventDefault(); clearTimeout(this._timer); this.load(1, true) }} className="organization-search service-search">
                        <div className="organization-search-icon">
                            <Icon name="search" size={17} />
                        </div>
                        <input placeholder="Имя, телефон, почта или точка" value={search} onChange={event => this.searchText(event.target.value)} />
                        {search.length > 0 &&
                            <div onClick={() => this.searchText("")} className="organization-search-clear">
                                <Icon name="close" size={11} width={2.4} />
                            </div>
                        }
                    </form>
                </div>

            </div>
        )
    }

    /* The numbers of the platform — the roles on one bar, the activity as tiles, the leaders of the month */
    _summary = () => {

        /* Fields */
        const { statistics, role, activity, leaders } = this.state

        if (!statistics) {
            return null
        }

        const parts = ["owner", "admin", "specialist"].map(key => ({ key, value: statistics[key], ...roleOf(key) }))

        const tiles = [
            { key: "busy", icon: "trend", value: statistics.busy, text: "с записями за 30 дней", accent: "green" },
            { key: "idle", icon: "clock", value: statistics.idle, text: "в команде, но без записей", accent: "orange" }
        ]

        return (
            <div className="master-summary">

                <div className="user-summary-main">

                    <div className="user-summary-top">
                        <div>
                            <b>{statistics.total.toLocaleString("ru-RU")}</b>
                            <span>{plural(statistics.total, "мастер", "мастера", "мастеров")} в Bookify Pro · {statistics.team.toLocaleString("ru-RU")} в командах</span>
                        </div>
                        <div className="user-summary-fresh">
                            <Icon name="trend" size={14} width={2.2} />
                            +{statistics.fresh.toLocaleString("ru-RU")} за 30 дней
                        </div>
                    </div>

                    <div className="user-summary-bar">
                        {parts.map(part =>
                            <i key={part.key} className={`master-${part.accent}`} style={{ flex: Math.max(part.value, 1) }} />
                        )}
                    </div>

                    <div className="user-summary-legend">
                        {parts.map(part =>
                            <div key={part.key} onClick={() => this.toggle("role", part.key, "")} className={`master-${part.accent} ${role === part.key ? "active" : ""}`}>
                                <i />
                                <span>{part.text}</span>
                                <b>{part.value.toLocaleString("ru-RU")}</b>
                            </div>
                        )}
                    </div>

                </div>

                <div className="master-summary-tiles">
                    {tiles.map(tile =>
                        <div
                            key={tile.key}
                            onClick={() => this.toggle("activity", tile.key, "")}
                            className={`user-tile ${tile.accent} clickable ${activity === tile.key ? "active" : ""}`}
                        >
                            <div className="user-tile-icon"><Icon name={tile.icon} size={16} width={2} /></div>
                            <b>{tile.value.toLocaleString("ru-RU")}</b>
                            <span>{tile.text}</span>
                        </div>
                    )}
                </div>

                <div className="master-leaders">
                    <div className="master-leaders-title">
                        <Icon name="crown" size={14} width={2} />
                        Больше всех записей за 30 дней
                    </div>
                    {leaders.length === 0 && <div className="master-leaders-empty">За месяц записей не было</div>}
                    {leaders.map((item, index) =>
                        <div key={`${item.id}`} onClick={() => this._master.current.open(item)} className="master-leader">
                            <em>{index + 1}</em>
                            <Avatar className="master-leader-avatar" src={item.image ? `${env.mediapoint}members/${item.image}` : null} alt={item.name}>
                                <span style={{ background: avatarColorOf(item.id) }}>{initialsOf(item.name)}</span>
                            </Avatar>
                            <div className="master-leader-text">
                                <b>{item.name ? item.name : "Без имени"}</b>
                                <span>{item.organization}</span>
                            </div>
                            <div className="master-leader-count">{item.bookings}</div>
                        </div>
                    )}
                </div>

            </div>
        )
    }

    _toolbar = () => {

        /* Fields */
        const { state, sort, count, statistics } = this.state

        return (
            <div className="service-toolbar">

                <div className="service-segment">
                    {STATES.map(item =>
                        <div key={item.key} onClick={() => this.refilter({ state: item.key })} className={state === item.key ? "active" : ""}>
                            {item.text}
                            {item.key === "none" && statistics && statistics.none > 0 && <span className="master-segment-count">{statistics.none}</span>}
                        </div>
                    )}
                </div>

                <div className="user-count">{count.toLocaleString("ru-RU")} {plural(count, "мастер", "мастера", "мастеров")}</div>

                <div className="service-sort">
                    <Icon name="sort" size={15} width={1.9} />
                    {SORTS.map(item =>
                        <div key={item.key} onClick={() => this.refilter({ sort: item.key })} className={sort === item.key ? "active" : ""}>{item.text}</div>
                    )}
                </div>

            </div>
        )
    }

    /* One master of the list */
    _item = item => {

        const image = item.image ? `${env.mediapoint}members/${item.image}` : null
        const name = item.name && `${item.name}`.trim().length > 0 ? `${item.name}`.trim() : null
        const roles = rolesOf(item)
        const done = item.bookings > 0 ? item.completed / item.bookings * 100 : 0
        const missed = item.bookings > 0 ? item.missed / item.bookings * 100 : 0
        const rest = item.organizations.length - 1

        return (
            <div key={`${item.id}`} onClick={() => this._master.current.open(item)} className={`master-item ${item.teams === 0 ? "free" : ""}`}>

                <div className="user-item-cell person">
                    <div className="user-item-avatar-wrap" style={{ "--avatar": avatarColorOf(item.id) }}>
                        <Avatar className="user-item-avatar" src={image} alt={name}>
                            {name ? <span>{initialsOf(name)}</span> : <Icon name="master" size={20} width={2} />}
                        </Avatar>
                        {item.push && <div className="user-item-push" title="Есть push"><Icon name="notification" size={9} width={2.6} /></div>}
                    </div>
                    <div className="user-item-title">
                        <div className={`user-item-name ${name ? "" : "empty"}`}>{name ? name : "Без имени"}</div>
                        <div className="user-item-phone">{item.phone ? item.phone : item.email}</div>
                    </div>
                </div>

                <div className="user-item-cell master-item-points">
                    {item.organizations.length > 0
                        ? <>
                            <b>{item.organizations[0]}</b>
                            <span>{rest > 0 ? `и ещё ${rest} ${plural(rest, "точка", "точки", "точек")}` : (item.profession ? item.profession : "одна точка")}</span>
                        </>
                        : <div className="user-item-muted">{item.memberships > 0 ? "Ушёл из команды" : "Без точки"}</div>
                    }
                </div>

                <div className="user-item-cell master-item-roles">
                    {roles.map(key => {
                        const role = roleOf(key)
                        return <div key={key} className={`master-role ${role.accent}`} title={role.text}><Icon name={role.icon} size={11} width={2.2} />{roles.length > 1 ? "" : role.text}</div>
                    })}
                    {roles.length === 0 && <div className="user-item-muted">—</div>}
                </div>

                <div className="user-item-cell visits">
                    {item.bookings > 0
                        ? <div className="user-item-visits">
                            <b>{item.bookings.toLocaleString("ru-RU")}</b>
                            <div className="user-item-visits-bar" title={`Состоялись: ${item.completed}, отмены и неявки: ${item.missed}`}>
                                <i className="done" style={{ width: `${done}%` }} />
                                <i className="missed" style={{ width: `${missed}%` }} />
                            </div>
                            <span>{item.month > 0 ? `+${item.month} за 30 дн.` : money(item.revenue)}</span>
                        </div>
                        : <div className="user-item-muted">Нет записей</div>
                    }
                </div>

                <div className="user-item-cell master-item-rating">
                    {item.rating
                        ? <>
                            <b><em>★</em>{ratingText(item.rating)}</b>
                            <span>{item.reviews} {plural(item.reviews, "отзыв", "отзыва", "отзывов")}</span>
                        </>
                        : <div className="user-item-muted">—</div>
                    }
                </div>

                <div className="user-item-cell last">
                    {item.lastVisit
                        ? <>
                            <b>{ago(item.lastVisit)}</b>
                            <span>последний визит</span>
                        </>
                        : <div className="user-item-muted">{ago(item.createdAt)}</div>
                    }
                </div>

            </div>
        )
    }

    _content = () => {

        /* Fields */
        const { status, data, count, page, limit } = this.state

        if (status !== "ok" || data.length === 0) {
            return (
                <div className="page-content">
                    {this._summary()}
                    {this._toolbar()}
                    <DataLayout status={status} reload={() => this.load(1, true)} data={data} onReset={() => this.refilter({ ...INITIAL })} />
                </div>
            )
        }

        return (
            <div className="page-content">

                {this._summary()}
                {this._toolbar()}

                <div className="service-list">

                    <div className="master-item head">
                        <div className="user-item-cell person">Мастер</div>
                        <div className="user-item-cell">Точка</div>
                        <div className="user-item-cell">Роль</div>
                        <div className="user-item-cell visits">Записи</div>
                        <div className="user-item-cell">Рейтинг</div>
                        <div className="user-item-cell last">Работал</div>
                    </div>

                    {data.map(item => this._item(item))}

                </div>

                <Pagination size={limit} page={page} total={count} onPageChange={page => { window.scrollTo(0, 0); this.load(page, true) }} />

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { navigate } = this.props

        return (
            <div className="page">

                {this._header()}

                {this._content()}

                <MasterModal ref={this._master} onOrganization={id => navigate(`/organization?open=${id}`)} />

            </div>
        )
    }
}

export default withRouter(Masters)
